/**
 * StepFlow user-facing version labels — pure functions.
 *
 * Format: ``"<sfn_version>.<minor:02d>"`` (e.g. "1.00", "1.10", "2.00").
 *   - MAJOR mirrors the SFN version. Bumps only on real topology change.
 *   - MINOR bumps by 10 for each subsequent release that reuses the same
 *     SFN version (config-only change).
 *   - Falls back to the legacy ``"v<release_version>"`` form when the
 *     release row has no ``stateMachineVersion`` (legacy / SANDBOX rows).
 *
 * Mirrors the backend helpers in
 * ``backend/api/rest/snapshots/snapshots_impl.py`` (``_format_stepflow_version``
 * + ``_compute_minor_for_sfn_version``). Keep the two in sync.
 */

/**
 * Build the label string for a given (sfn_version, minor) pair.
 * Returns "" when sfn_version is missing — the caller falls back to ``v{N}``.
 */
export function formatStepflowVersion(sfnVersion, minor) {
  const v = Number(sfnVersion);
  if (!Number.isFinite(v) || v < 1) return '';
  const m = Number(minor);
  const minorPart = Number.isFinite(m) && m >= 0 ? Math.trunc(m) : 0;
  return `${v}.${String(minorPart).padStart(2, '0')}`;
}

/**
 * Count how many releases in ``releases`` already point at the same
 * ``sfn_version`` as ``targetRelease``, excluding the target itself.
 *
 * ``targetRelease`` can be either the release row (with ``releaseVersion``
 * + ``stateMachineVersion``), or just a ``releaseVersion`` integer when the
 * caller is asking "what would the label be for v{N} given this list."
 *
 * Returns the minor (0, 10, 20, ...). Returns 0 if no SFN version is
 * known (caller falls back to legacy ``v{N}`` label).
 */
export function computeMinorForRelease(releases, targetRelease) {
  if (!Array.isArray(releases) || releases.length === 0) return 0;
  const target = typeof targetRelease === 'object' && targetRelease !== null
    ? targetRelease
    : null;
  const targetReleaseVersion = target
    ? Number(target.releaseVersion)
    : Number(targetRelease);
  if (!Number.isFinite(targetReleaseVersion) || targetReleaseVersion < 1) return 0;

  // Find the target row's sfn_version. If target is a row, use its sfn directly.
  // Otherwise look it up in the list.
  let targetSfn = target ? Number(target.stateMachineVersion) : NaN;
  if (!Number.isFinite(targetSfn) || targetSfn < 1) {
    const found = releases.find(
      (r) => Number(r?.releaseVersion) === targetReleaseVersion,
    );
    targetSfn = Number(found?.stateMachineVersion);
  }
  if (!Number.isFinite(targetSfn) || targetSfn < 1) return 0;

  // Count releases on the same SFN version with releaseVersion <= target's,
  // excluding the target itself. This gives the position of the target
  // among same-SFN releases, in chronological order.
  let priorSameSfn = 0;
  for (const r of releases) {
    if (!r) continue;
    const rv = Number(r.releaseVersion);
    const sfn = Number(r.stateMachineVersion);
    if (!Number.isFinite(rv) || rv < 1) continue;
    if (!Number.isFinite(sfn) || sfn < 1) continue;
    if (sfn !== targetSfn) continue;
    if (rv >= targetReleaseVersion) continue;
    priorSameSfn += 1;
  }
  return priorSameSfn * 10;
}

/**
 * Resolve a release row to its display label, given the full releases list
 * for context. Returns ``"1.00"``, ``"1.10"``, etc. Falls back to
 * ``"v{releaseVersion}"`` when ``stateMachineVersion`` is missing.
 *
 * ``release`` can be either a release object or a plain ``releaseVersion``
 * number — convenient for places that only know "I want v3 labeled."
 */
export function releaseLabel(releases, release) {
  if (release === null || release === undefined) return '';
  const releaseObj = typeof release === 'object'
    ? release
    : (Array.isArray(releases)
        ? releases.find((r) => Number(r?.releaseVersion) === Number(release))
        : null) || { releaseVersion: Number(release) };

  const rv = Number(releaseObj.releaseVersion);
  if (!Number.isFinite(rv) || rv < 1) return '';

  const sfn = Number(releaseObj.stateMachineVersion);
  if (!Number.isFinite(sfn) || sfn < 1) {
    // Legacy / SANDBOX row — fall back to vN form
    return `v${rv}`;
  }
  const minor = computeMinorForRelease(releases, releaseObj);
  return formatStepflowVersion(sfn, minor);
}

/**
 * Resolve the latest released label for a network row. Convenience wrapper
 * for the header / sidebar / room badge surfaces that only know
 * ``network.releaseVersion`` and have an optional releases list.
 */
export function latestReleaseLabel(releases, networkReleaseVersion) {
  return releaseLabel(releases, networkReleaseVersion);
}

/**
 * DISPLAY label for a release/pool/room row — the single entry point every UI
 * surface should use. Prefers the AUTHORITATIVE backend ``stepflowVersion``
 * (no client recompute, no flicker); falls back to the client-side
 * ``releaseLabel`` only when the server didn't stamp one (rolling deploy /
 * legacy). NEVER returns a raw ``vN`` — the old scheme is retired. Returns ''
 * only when there's genuinely no version to show.
 *
 * @param {{ stepflowVersion?: string, releaseVersion?: number } | number} row
 *        a row object (release/pool) or a bare releaseVersion number
 * @param {Array} [releases] full releases list for the fallback compute
 */
export function displayVersionLabel(row, releases) {
  const strip = (fb) => (fb && !/^v\d+$/i.test(fb) ? fb : '');
  // Prefer the AUTHORITATIVE server ``stepflowVersion`` computed on the matching release row.
  // This is the ONLY label source that works for GENERATIVE (manifest) networks — they have no
  // ``stateMachineVersion``, so the client-side ``releaseLabel`` below can only produce a "vN"
  // (which ``strip`` then kills → empty chip). The backend serializer stamps ``stepflowVersion``
  // ("1.00" etc.) on every release row for BOTH kinds; find it whether we were handed a row
  // object or a bare releaseVersion number.
  const rvNum = (row && typeof row === 'object') ? Number(row.releaseVersion) : Number(row);
  const serverRow = (row && typeof row === 'object' && row.stepflowVersion)
    ? row
    : (Array.isArray(releases)
        ? releases.find((r) => Number(r?.releaseVersion) === rvNum)
        : null);
  const serverLabel = String(serverRow?.stepflowVersion || '').trim();
  if (serverLabel) return serverLabel;
  if (row && typeof row === 'object') {
    // Client fallback (ASL rolling-deploy / no server label): if the row itself carries an SFN
    // version, label from it; otherwise resolve BY releaseVersion against the list.
    const hasSfn = Number(row.stateMachineVersion) >= 1;
    const fb = hasSfn ? releaseLabel(releases, row) : releaseLabel(releases, Number(row.releaseVersion));
    return strip(fb);
  }
  return strip(releaseLabel(releases, row));
}

/**
 * Predict the user-facing label for the *next* release that hasn't been
 * cut yet. Used by the release dialog to show "1.00 → 1.10" or "1.00 → 2.00"
 * before the backend commits the release row.
 *
 * Decision logic mirrors the backend's ``_resolve_topology_pin_target``:
 *   - ``topologyChanged = true`` → new SFN version → MAJOR bump → ``N.00``
 *   - ``topologyChanged = false`` → same SFN version reused → MINOR bump → ``N.M0``
 *
 * @param {Array} releases — full releases list (same shape as listNetworkReleases)
 * @param {boolean} topologyChanged — whether a new SFN version will be cut
 * @returns {string} e.g. "2.00" or "1.10"
 */
export function predictNextVersionLabel(releases, topologyChanged) {
  const active = (Array.isArray(releases) ? releases : [])
    .filter((r) => Number.isFinite(Number(r?.stateMachineVersion)) && Number(r.stateMachineVersion) >= 1)
    .filter((r) => Number.isFinite(Number(r?.releaseVersion)) && Number(r.releaseVersion) >= 1);

  if (active.length === 0) {
    return '1.00';
  }

  if (topologyChanged) {
    const maxSfn = active.reduce((mx, r) => Math.max(mx, Number(r.stateMachineVersion)), 0);
    return formatStepflowVersion(maxSfn + 1, 0);
  }

  const latestRelease = active.reduce((best, r) =>
    Number(r.releaseVersion) > Number(best.releaseVersion) ? r : best,
  active[0]);
  const latestSfn = Number(latestRelease.stateMachineVersion);
  const sameSfnCount = active.filter((r) => Number(r.stateMachineVersion) === latestSfn).length;
  return formatStepflowVersion(latestSfn, sameSfnCount * 10);
}
